import { Suspense, lazy } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { type AuthOrg, type AuthState, type AuthUser, fetchMe } from './auth.js';
import { AcceptInvite } from './pages/AcceptInvite.js';
import { Approvals } from './pages/Approvals.js';
import { Audit } from './pages/Audit.js';
import { Billing } from './pages/Billing.js';
import { Compliance } from './pages/Compliance.js';
import { Diff } from './pages/Diff.js';
import { Engage } from './pages/Engage.js';
import { HostedScans } from './pages/HostedScans.js';
import { Install } from './pages/Install.js';
import { Keys } from './pages/Keys.js';
import { Login } from './pages/Login.js';
import { Members } from './pages/Members.js';
import { Overview } from './pages/Overview.js';
import { Portfolio } from './pages/Portfolio.js';
import { Products } from './pages/Products.js';
import { Remediations } from './pages/Remediations.js';
import { Review } from './pages/Review.js';
import { Scans } from './pages/Scans.js';
import { Settings } from './pages/Settings.js';
import { Webhooks } from './pages/Webhooks.js';

// Internal consoles + the playground are code-split so regular customers
// never download them; they load on demand behind Suspense.
const Admin = lazy(() => import('./pages/Admin.js').then((m) => ({ default: m.Admin })));
const Founder = lazy(() => import('./pages/Founder.js').then((m) => ({ default: m.Founder })));
const FounderMetrics = lazy(() =>
  import('./pages/FounderMetrics.js').then((m) => ({ default: m.FounderMetrics })),
);
const Playground = lazy(() =>
  import('./pages/Playground.js').then((m) => ({ default: m.Playground })),
);

const lazyFallback = (
  <p style={{ padding: 32, color: 'var(--fg-muted)' }}>
    <output>Loading…</output>
  </p>
);

type Page =
  | 'overview'
  | 'install'
  | 'keys'
  | 'members'
  | 'webhooks'
  | 'engage'
  | 'products'
  | 'portfolio'
  | 'scans'
  | 'hosted-scans'
  | 'diff'
  | 'review'
  | 'fixes'
  | 'compliance'
  | 'approvals'
  | 'billing'
  | 'audit'
  | 'settings';

const PAGES: Array<{ id: Page; label: string; icon: string }> = [
  { id: 'overview', label: 'Overview', icon: '◆' },
  { id: 'install', label: 'Install', icon: '⌘' },
  { id: 'keys', label: 'API keys', icon: '🔑' },
  { id: 'members', label: 'Members', icon: '👥' },
  { id: 'webhooks', label: 'Webhooks', icon: '⇆' },
  { id: 'engage', label: 'New engagement', icon: '✦' },
  { id: 'products', label: 'Products', icon: '▣' },
  { id: 'portfolio', label: 'Portfolio', icon: '📊' },
  { id: 'scans', label: 'Scans', icon: '◎' },
  { id: 'hosted-scans', label: 'Hosted scans', icon: '☁' },
  { id: 'diff', label: 'Diff', icon: '◇' },
  { id: 'review', label: 'Review & publish', icon: '☑' },
  { id: 'fixes', label: 'Fixes', icon: '🛠' },
  { id: 'compliance', label: 'Compliance', icon: '⚖' },
  { id: 'approvals', label: 'VPAT approvals', icon: '✓' },
  { id: 'billing', label: 'Billing', icon: '$' },
  { id: 'audit', label: 'Audit log', icon: '⏱' },
  { id: 'settings', label: 'Settings', icon: '⚙' },
];

const inviteTokenFromPath = (): string | null => {
  if (typeof window === 'undefined') return null;
  const m = /^\/invites\/([^/?#]+)/.exec(window.location.pathname);
  return m?.[1] ?? null;
};

const isAdminPath = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/admin');
};

const isPlaygroundPath = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/playground');
};

const isFounderPath = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/founder');
};

const isFounderMetricsPath = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/founder/metrics');
};

type AppState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated' }
  | { kind: 'no-org'; user: AuthUser }
  | { kind: 'ready'; user: AuthUser; orgs: AuthOrg[]; activeOrg: AuthOrg };

export function App(): preact.ComponentChildren {
  const [state, setState] = useState<AppState>({ kind: 'loading' });
  const [page, setPage] = useState<Page>('overview');
  const inviteToken = inviteTokenFromPath();
  if (inviteToken) {
    return <AcceptInvite token={inviteToken} />;
  }
  if (isAdminPath()) {
    return (
      <Suspense fallback={lazyFallback}>
        <Admin />
      </Suspense>
    );
  }
  // Checked before isFounderPath: /founder/metrics is inside its prefix.
  if (isFounderMetricsPath()) {
    return (
      <Suspense fallback={lazyFallback}>
        <FounderMetrics />
      </Suspense>
    );
  }
  if (isFounderPath()) {
    return (
      <Suspense fallback={lazyFallback}>
        <Founder />
      </Suspense>
    );
  }
  if (isPlaygroundPath()) {
    return (
      <Suspense fallback={lazyFallback}>
        <Playground />
      </Suspense>
    );
  }

  const loadAuth = async () => {
    try {
      const me: AuthState = await fetchMe();
      if (me.orgs.length === 0) {
        setState({ kind: 'no-org', user: me.user });
      } else {
        const first = me.orgs[0];
        if (first) setState({ kind: 'ready', user: me.user, orgs: me.orgs, activeOrg: first });
      }
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status === 401) setState({ kind: 'unauthenticated' });
      else setState({ kind: 'unauthenticated' }); // any error → bounce to login
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadAuth is stable per render and we want it to run once on mount only
  useEffect(() => {
    void loadAuth();
  }, []);

  // Allow inter-page navigation via custom event (used by Install page).
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<Page>).detail;
      if (PAGES.some((p) => p.id === detail)) setPage(detail);
    };
    window.addEventListener('whs-nav', handler);
    return () => window.removeEventListener('whs-nav', handler);
  }, []);

  if (state.kind === 'loading') {
    return (
      <div style={{ padding: 32, color: 'var(--fg-muted)' }}>
        <output>Loading session…</output>
      </div>
    );
  }

  if (state.kind === 'unauthenticated') {
    return <Login />;
  }

  if (state.kind === 'no-org') {
    return (
      <div style={{ padding: 48, maxWidth: 600, margin: '0 auto' }}>
        <h1>No organization found</h1>
        <p>
          Your account ({state.user.email}) isn't a member of any organization yet. Contact your
          admin or <a href="mailto:support@wholisphere.ai">support</a>.
        </p>
      </div>
    );
  }

  const { user, orgs, activeOrg } = state;
  const switchOrg = (orgId: string) => {
    const next = orgs.find((o) => o.id === orgId);
    if (next) setState({ kind: 'ready', user, orgs, activeOrg: next });
  };

  return (
    <div class="app">
      <a href="#main" class="skip-link">
        Skip to main content
      </a>
      <aside class="sidebar" aria-label="Primary navigation">
        <div class="brand">
          <img class="logo" src="/logo.jpg" alt="" width="24" height="24" />
          Wholisphere
        </div>
        {orgs.length > 1 && (
          <div class="org-switcher">
            <label htmlFor="active-org" class="small muted">
              Organization
            </label>
            <select
              id="active-org"
              value={activeOrg.id}
              onChange={(e) => switchOrg((e.target as HTMLSelectElement).value)}
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <nav class="nav">
          {PAGES.map((p) => (
            <button
              key={p.id}
              class="nav-item"
              type="button"
              aria-current={page === p.id ? 'page' : undefined}
              onClick={() => setPage(p.id)}
            >
              <span aria-hidden="true">{p.icon}</span>
              {p.label}
            </button>
          ))}
        </nav>
        <div class="sidebar-footer">
          <div>v0.0.0</div>
          <div class="small muted">{user.email}</div>
        </div>
      </aside>
      <main id="main" tabIndex={-1}>
        {page === 'overview' && <Overview user={user} org={activeOrg} />}
        {page === 'install' && <Install org={activeOrg} />}
        {page === 'keys' && <Keys org={activeOrg} />}
        {page === 'members' && <Members user={user} org={activeOrg} />}
        {page === 'webhooks' && <Webhooks org={activeOrg} />}
        {page === 'engage' && <Engage org={activeOrg} />}
        {page === 'products' && <Products org={activeOrg} />}
        {page === 'portfolio' && <Portfolio org={activeOrg} />}
        {page === 'scans' && <Scans org={activeOrg} />}
        {page === 'hosted-scans' && <HostedScans org={activeOrg} />}
        {page === 'diff' && <Diff org={activeOrg} />}
        {page === 'review' && <Review org={activeOrg} />}
        {page === 'compliance' && <Compliance org={activeOrg} />}
        {page === 'fixes' && <Remediations org={activeOrg} />}
        {page === 'approvals' && <Approvals org={activeOrg} />}
        {page === 'billing' && <Billing org={activeOrg} />}
        {page === 'audit' && <Audit org={activeOrg} />}
        {page === 'settings' && (
          <Settings
            user={user}
            org={activeOrg}
            onSignOut={() => setState({ kind: 'unauthenticated' })}
          />
        )}
      </main>
    </div>
  );
}
